/* eslint-disable no-bitwise */
import _ from 'underscore';
import targetConstants from '@bingads-webui-campaign-legacy/base-targets/viewmodel/targetConstants';
import targetTypes from '@bingads-webui-campaign-legacy/base-targets/viewmodel/targetTypes';
import { validateBid } from '@bingads-webui-campaign-legacy/base-targets/util/bidValidator';
import { getBulkApiErrors } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import ko from 'knockout';
import pageContext from '@bingads-webui-static-legacy/page-context';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import { isCashbackPercentValid, getCashbackPercentValidationError, getCashbackInheritOptions } from '@bingads-webui-campaign/target-bid-adjustment';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

export default class BaseTargetViewModel {
  constructor(options = {}) {
    this.options = options;
    this.targetTypePrefix = options.targetTypePrefix;
    this.targetRowKeys = options.targetRowKeys;
    this.criterionType = options.criterionType;
    this.criterionTypeTarget = options.criterionTypeTarget;
    this.MTBidAdjustmentErrorCode = options.MTBidAdjustmentErrorCode;
    this.MTBidAdjustmentErrorNumber = options.MTBidAdjustmentErrorNumber;
    this.targetPopupId = options.targetPopupId;
    this.rowExtenders = options.rowExtenders;

    this.targetDescription = i18n.get(`${this.targetTypePrefix}_Description`);
    this.targetHeader = i18n.get(`${this.targetTypePrefix}_Header`);
    this.bidAdjustmentHeader = i18n.get('Target_ColumnHeaderBidAdjustment');
    this.level = options.level;
    this.useCampaignSettings = ko.observable(false);
    this.useCampaignSettings.subscribe(this.onCampaignSettingsCheckChange, this);
    this.showAdGroupOverrideInformation = ko.observable(false);
    this.isForVNext = ko.observable(options.isForVNext);
    this.readonlyBid = ko.computed(this.computeReadonlyBid, this);
    this.rows = [];
    this.targetTypeToErrorProperty = {};
    this.logInteraction = _.once(() => {
      if (this.options.activityLogger) {
        this.options.activityLogger.signal(`${this.options.targetType} bid adjustment changed`, `${this.options.targetType} target`);
      }
    });
    this.supportCashbackSettings = ko.observable(options.supportCashbackSettings && !pageContext.Permissions.IsPersonalizedOffersV3Enabled);
    this.currentCampaign = options.currentCampaign;
    this.currentAdGroup = options.currentAdGroup;
    this.populateRows(options);
  }

  populateRows(dataset) {
    _.each(_.keys(dataset), (key) => {
      if (_.has(this.options, key)) {
        this.options[key] = dataset[key];
      }
    });

    _.each(this.targetRowKeys, (targetRowKey) => {
      let row;

      this[`${this.targetTypePrefix}${targetRowKey}Row`] = row = {
        targetKey: targetRowKey,
        targetName: i18n.get(`${this.targetTypePrefix}_${targetRowKey}`),
        bidAdjustmentOptions: this.targetTypePrefix === 'MultimediaAdType' ? targetConstants.multimediaBidAdjustmentOptions : targetConstants.bidAdjustmentOptions,
        selectedBidAdjustmentOption: ko.observable(this.getSelectedBidAdjustmentOption(targetRowKey, dataset)),
        bidAdjustmentValue: ko.observable(this.getBidAdjustmentValue(targetRowKey, dataset)),
        koErrorObservable: `${this.targetTypePrefix}${targetRowKey}ErrorObservable`,
        cashbackPercentValue: ko.observable(this.getCashbackPercentValue(targetRowKey, dataset)),
        koErrorType: `${this.targetTypePrefix}${targetRowKey}ErrorType`,
      };
      row.bidAdjustmentValue.subscribe(() => {
        this.validate();
        this.logInteraction();
      }, this);
      row.selectedBidAdjustmentOption.subscribe(() => {
        this.validate();
        this.logInteraction();
      }, this);
      row.cashbackPercentValue.subscribe(() => {
        this.validate();
        this.logInteraction();
      });
      const campaignCashbackPercent = dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackPercent`];
      const adGroupCashbackPercent = dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackPercent`];

      this[`original${this.targetTypePrefix}${targetRowKey}CampaignBidAdjustmentValue`] = dataset[`${this.targetTypePrefix}${targetRowKey}CampaignBidAdjustment`];
      this[`original${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustmentValue`] = dataset[`${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustment`];
      this[`original${this.targetTypePrefix}${targetRowKey}CampaignCashbackPercentValue`] = campaignCashbackPercent;
      this[`original${this.targetTypePrefix}${targetRowKey}AdgroupCashbackPercentValue`] = adGroupCashbackPercent;
      this[`${this.targetTypePrefix}${targetRowKey}CampaignBidId`] = dataset[`${this.targetTypePrefix}${targetRowKey}CampaignBidId`];
      this[`${this.targetTypePrefix}${targetRowKey}AdgroupBidId`] = dataset[`${this.targetTypePrefix}${targetRowKey}AdgroupBidId`];
      this[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackPercentValue`] = campaignCashbackPercent;
      this[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackPercentValue`] = adGroupCashbackPercent;
      this[`${this.targetTypePrefix}${targetRowKey}ErrorObservable`] = ko.observable(true).extend({
        validate: [],
      });
      this[`${this.targetTypePrefix}${targetRowKey}ErrorType`] = ko.observable(null);

      let currentRow = this[`${this.targetTypePrefix}${targetRowKey}Row`];

      if (_.has(this.rowExtenders, targetRowKey)) {
        currentRow = _.extend(currentRow, this.rowExtenders[targetRowKey]);
      }

      this.rows.push(currentRow);
      this.targetTypeToErrorProperty[`${targetRowKey}`] = `${this.targetTypePrefix}${targetRowKey}ErrorObservable`;
    });

    //uncheck "Use my campaign settings" checkbox if one row having its own value
    const anyAdGroupValue = _.some(this.targetRowKeys, (targetRowKey) => {
      let targetHasValue = false;

      if (this.options.level === 'adgroup') {
        if (this.options.scenario === 'adgroup-create') {
          if (targetRowKey === 'MultimediaAdsBids') {
            return false;
          }
          const targetRow = this[`${this.targetTypePrefix}${targetRowKey}Row`];

          targetHasValue = targetRow.bidAdjustmentValue() !== 0 ||
            !this.isCashbackValueEmpty(targetRow.cashbackPercentValue());
        } else if (this.options.scenario === 'adgroup-settings') {
          if (targetRowKey === 'NativeAdsBids') {
            targetHasValue = !_.isNull(this.options[`${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustment`]) ||
              !this.isCashbackValueEmpty(this.options[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackPercent`]);
          } else if (targetRowKey === 'MultimediaAdsBids') {
            targetHasValue = !_.isNull(this.options[`${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustment`]);
          } else {
            targetHasValue = this[`${this.targetTypePrefix}${targetRowKey}AdgroupBidId`] > 0;
          }
        }
      }

      return targetHasValue;
    });

    if (this.options.level === 'adgroup' && !anyAdGroupValue) {
      this.useCampaignSettings(true);
      this.setShowAdGroupOverrideInformation();
    }

    this.setupCashbackValuesForAllRows();

    this.useCampaignSettings.subscribe(() => {
      this.resetCashbackInheritInfoInAllRows();
    });
  }

  setupCashbackValuesForAllRows() {
    const dataset = this.options;

    _.each(this.targetRowKeys, (targetRowKey) => {
      const row = this[`${this.targetTypePrefix}${targetRowKey}Row`];

      const campaignCashbackPercent = dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackPercent`];
      const adGroupCashbackPercent = dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackPercent`];

      this.setCashbackInheritInfo({
        campaignCashbackPercent,
        adGroupCashbackPercent,
        targetRowKey,
        row,
        dataset,
      });

      row.cashbackOptions = ko.observable(this.getCashbackOptions(targetRowKey, dataset));
      row.selectedCashbackOption = ko.observable(this.getSelectedCashbackOption(targetRowKey, dataset));
      row.cashbackInheritValue = ko.observable(this.getCashbackInheritValue(targetRowKey, dataset));
      row.snapshotOverrideCashbackPercent = ko.observable(row.cashbackPercentValue());

      row.selectedCashbackOption.subscribe((currentSelectedOption) => {
        if (currentSelectedOption === 'override') {
          row.snapshotOverrideCashbackPercent(row.cashbackPercentValue());
        }
      }, row, 'beforeChange');

      row.selectedCashbackOption.subscribe((selectedOption) => {
        if (selectedOption === 'override') {
          row.cashbackPercentValue(row.snapshotOverrideCashbackPercent());
        } else if (selectedOption === 'noCashback') {
          row.cashbackPercentValue(0);
        } else {
          row.cashbackPercentValue(null);
        }
      });
    });
  }

  findTargetKey(entity) {
    switch (entity.CriterionType) {
    case targetTypes.Device:
      return entity.Criterion.DeviceCriterion.DeviceName;
    case targetTypes.Age:
      return entity.Criterion.AgeCriterion.Target;
    case targetTypes.Gender:
      return entity.Criterion.GenderCriterion.Target;
    default:
      break;
    }
    return '';
  }

  setData(updatingEntity) {
    const updatingEntityRowKey = this.findTargetKey(updatingEntity);
    const levelText = this.level === 'campaign' ? 'Campaign' : 'Adgroup';
    const key = `${this.targetTypePrefix}${updatingEntityRowKey}${levelText}BidId`;

    if (updatingEntity.Status === 'Deleted') {
      this[key] = this.options[key] = '';
      return;
    }

    if (updatingEntity.CriterionType === targetTypes.Device && updatingEntityRowKey === 'Computers') {
      this[key] = this.options[key] = updatingEntity.Id;
      return;
    }

    _.each(this.rows, (row) => {
      if (row.targetKey === updatingEntityRowKey) {
        this[key] = this.options[key] = updatingEntity.Id;
      }
    });
  }

  setLevel(level) {
    this.options.level = this.level = level;
  }

  computeReadonlyBid() {
    return this.level === 'adgroup' && this.useCampaignSettings();
  }

  onCampaignSettingsCheckChange() {
    _.each(this.targetRowKeys, (targetRowKey) => {
      this[`${this.targetTypePrefix}${targetRowKey}Row`].selectedBidAdjustmentOption(this.getSelectedBidAdjustmentOption(targetRowKey, this.options));
      this[`${this.targetTypePrefix}${targetRowKey}Row`].bidAdjustmentValue(this.getBidAdjustmentValue(targetRowKey, this.options));
      if (this[`${this.targetTypePrefix}${targetRowKey}Row`].snapshotOverrideCashbackPercent) {
        this[`${this.targetTypePrefix}${targetRowKey}Row`].snapshotOverrideCashbackPercent(this.getCashbackPercentValue(targetRowKey, this.options));
      }
      this[`${this.targetTypePrefix}${targetRowKey}Row`].cashbackPercentValue(this.getCashbackPercentValue(targetRowKey, this.options));
    });
  }

  getSelectedBidAdjustmentOption(targetRowKey, dataset) {
    if (this.options.level === 'campaign' || this.useCampaignSettings()) {
      return dataset[`${this.targetTypePrefix}${targetRowKey}CampaignBidAdjustment`] >= 0 ? targetConstants.bidAdjustmentIncreaseValue : targetConstants.bidAdjustmentDecreaseValue;
    } else {
      return dataset[`${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustment`] >= 0 ? targetConstants.bidAdjustmentIncreaseValue : targetConstants.bidAdjustmentDecreaseValue;
    }
  }

  getBidAdjustmentValue(targetRowKey, dataset) {
    if (this.options.level === 'campaign' || this.useCampaignSettings()) {
      return Math.abs(dataset[`${this.targetTypePrefix}${targetRowKey}CampaignBidAdjustment`]);
    } else {
      if (targetRowKey === 'MultimediaAdsBids') {
        return Math.abs(dataset[`${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustment`] ? dataset[`${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustment`] : targetConstants.multimediaAdDefaultBid);
      }

      return Math.abs(dataset[`${this.targetTypePrefix}${targetRowKey}AdgroupBidAdjustment`]);
    }
  }

  getCashbackPercentValue(targetRowKey, dataset) {
    if (this.options.level === 'campaign' || this.useCampaignSettings()) {
      return dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackPercent`];
    } else {
      return dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackPercent`];
    }
  }

  getCashbackOptions(targetRowKey, dataset) {
    if (this.options.level === 'campaign' || this.useCampaignSettings()) {
      return dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackOptions`];
    }
    return dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackOptions`];
  }

  getSelectedCashbackOption(targetRowKey, dataset) {
    if (this.options.level === 'campaign' || this.useCampaignSettings()) {
      return dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackSelectedOption`];
    }
    return dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackSelectedOption`];
  }

  getCashbackInheritValue(targetRowKey, dataset) {
    let value;

    if (this.options.level === 'campaign' || this.useCampaignSettings()) {
      value = dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackInheritValue`];
    }
    value = dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackInheritValue`];
    return value === '-' ? value : decimal.fromFloatToString(value, 2);
  }

  getValues() {
    let values = [];

    //if we're in the adgroup level and the useCampaignSettings checkbox is checked, return an empty list as the adgroup targets use their campaign level settings
    //TODO[imang]: this is only a true logic in create mode. in the update mode: we have to set adgroup targets for delete if using campaign level settings
    if (this.options.scenario === 'adgroup-create' && this.useCampaignSettings()) {
      return values;
    } else if (this.options.scenario === 'adgroup-settings' && this.useCampaignSettings()) {
      _.each(this.targetRowKeys, (targetRowKey) => {
        if (this[`${this.targetTypePrefix}${targetRowKey}AdgroupBidId`] > 0) {
          let criterion = {
            'Type': this.criterionType,
          };

          criterion[`${this.criterionType}Criterion`] = {};
          criterion[`${this.criterionType}Criterion`][`${this.criterionTypeTarget}`] = targetRowKey;

          values.push({
            'Id': this[`${this.targetTypePrefix}${targetRowKey}AdgroupBidId`],
            'CriterionType': this.criterionType,
            'Criterion': criterion,
            'Status': 'Deleted',
          });
        }
      });

      return values;
    }

    let levelPrefix = this.options.level === 'campaign' || this.useCampaignSettings() ? 'Campaign' : 'Adgroup';
    let anyDeviceChange = this.findIfThereIsAnyDeviceChange(levelPrefix);

    _.each(this.targetRowKeys, (targetRowKey) => {
      const currentRow = this[`${this.targetTypePrefix}${targetRowKey}Row`];
      let rowBidAdjustment = Number(currentRow.bidAdjustmentValue()) * currentRow.selectedBidAdjustmentOption();
      const rowCashbackPercent = currentRow.cashbackPercentValue();

      //consider the row for create/update only if
      //there is a BidId (update):
      //  if in adgroup-settings and useCampaignSettings
      //    true
      //  else
      //    input bid is different from original bid
      //there is no BidId (create):
      //  input bid is not zero
      let addEntry = false;

      if (this[`${this.targetTypePrefix}${targetRowKey}${levelPrefix}BidId`]) {
        if (this.options.level === 'adgroup' && this.useCampaignSettings()) {
          addEntry = true;
        } else {
          addEntry = rowBidAdjustment !== this[`original${this.targetTypePrefix}${targetRowKey}${levelPrefix}BidAdjustmentValue`] ||
            this.isCashbackValuesEqual(rowCashbackPercent, this[`original${this.targetTypePrefix}${targetRowKey}${levelPrefix}CashbackPercentValue`]);
        }
      } else if (this.options.level === 'adgroup' && !this.useCampaignSettings()) {
        addEntry = true;
      } else {
        addEntry = rowBidAdjustment !== 0 || !this.isCashbackValueEmpty(rowCashbackPercent);
      }

      //this is a workaround to always send all three device types to MT no matter what. They don't work if we don't provide all device target types.
      if (this.criterionType === 'Device' && anyDeviceChange) {
        addEntry = true;
      }

      if (addEntry) {
        let criterion = {
          'Type': this.criterionType,
          'BidAdjustment': rowBidAdjustment,
        };

        if (this.supportCashbackSettings()) {
          criterion.CashbackPercent = rowCashbackPercent;
        }

        criterion[`${this.criterionType}Criterion`] = {};
        criterion[`${this.criterionType}Criterion`][`${this.criterionTypeTarget}`] = targetRowKey;

        values.push({
          'Id': this[`${this.targetTypePrefix}${targetRowKey}${levelPrefix}BidId`] || '',
          'CriterionType': this.criterionType,
          'Criterion': criterion,
        });
      }
    });

    return values;
  }

  findIfThereIsAnyDeviceChange(levelPrefix) {
    let result = false;

    if (this.criterionType === 'Device') {
      _.each(this.targetRowKeys, (targetRowKey) => {
        const currentRow = this[`${this.targetTypePrefix}${targetRowKey}Row`];
        let rowBidAdjustment = Number(currentRow.bidAdjustmentValue()) * currentRow.selectedBidAdjustmentOption();
        const rowCashbackPercent = currentRow.cashbackPercentValue();

        if (this[`${this.targetTypePrefix}${targetRowKey}${levelPrefix}BidId`]) {
          if (this.options.level === 'adgroup' && this.useCampaignSettings()) {
            result = true;
          } else {
            result = result || rowBidAdjustment !== this[`original${this.targetTypePrefix}${targetRowKey}${levelPrefix}BidAdjustmentValue`] ||
              rowCashbackPercent !== this[`original${this.targetTypePrefix}${targetRowKey}${levelPrefix}CashbackPercentValue`];
          }
        } else if (this.options.level === 'adgroup' && !this.useCampaignSettings()) {
          result = true;
        } else {
          result = result || rowBidAdjustment !== 0 || rowCashbackPercent !== '';
        }
      });
    }
    return result;
  }

  isInRange(rowBid, targetRowKey) {
    if (targetRowKey === 'MultimediaAdsBids') {
      return this[`${this.targetTypePrefix}${targetRowKey}Row`].selectedBidAdjustmentOption() === targetConstants.bidAdjustmentIncreaseValue &&
        rowBid <= targetConstants.maximumBid[`${this.targetTypePrefix}_${targetRowKey}`] &&
        rowBid >= targetConstants.minimumBid[`${this.targetTypePrefix}_${targetRowKey}`];
    }

    return rowBid >= 0 &&
      (this[`${this.targetTypePrefix}${targetRowKey}Row`].selectedBidAdjustmentOption() === targetConstants.bidAdjustmentIncreaseValue ?
        rowBid <= targetConstants.maximumBid[`${this.targetTypePrefix}_${targetRowKey}`] :
        rowBid <= targetConstants.minimumBid[`${this.targetTypePrefix}_${targetRowKey}`]);
  }

  validate() {
    if (!this.errors) {
      return true;
    }
    let bidErrors = [];
    const cashbackErrors = [];
    let isValid = true;

    _.each(this.targetRowKeys, (targetRowKey) => {
      const row = this[`${this.targetTypePrefix}${targetRowKey}Row`];
      const rowBid = row.bidAdjustmentValue();

      let isInRange = this.isInRange(rowBid, targetRowKey);
      const {
        isNotEmpty,
        isDecimal,
        isNonNegative,
        isEndingWithDigit,
      } = validateBid(rowBid);

      let bidValid = isNotEmpty && isInRange && isDecimal && isNonNegative && isEndingWithDigit;

      const rowCashbackPercent = row.cashbackPercentValue();
      const cashbackPercentValidated = !this.supportCashbackSettings() || isCashbackPercentValid(rowCashbackPercent, decimal);

      isValid = isValid && bidValid && cashbackPercentValidated;


      this[`${this.targetTypePrefix}${targetRowKey}ErrorObservable`](bidValid);

      if (isValid) {
        this[`${this.targetTypePrefix}${targetRowKey}ErrorType`](null);
      } else {
        this[`${this.targetTypePrefix}${targetRowKey}ErrorType`](cashbackPercentValidated ? 'BID' : 'CASHBACK');
      }

      if (!isNotEmpty) {
        bidErrors.push({
          Error: i18n.get('AdvancedTargeting_Error_BidValueMustNotBeEmpty'),
          ErrorNumber: '',
          Property: `${this.targetTypePrefix}${targetRowKey}ErrorObservable`,
        });
      } else if (!isDecimal || !isNonNegative || !isEndingWithDigit) {
        bidErrors.push({
          Error: i18n.get('Validation_InvalidBidAdjustment'),
          ErrorNumber: '',
          Property: `${this.targetTypePrefix}${targetRowKey}ErrorObservable`,
        });
      } else if (!isInRange) {
        bidErrors.push({
          Error: this.MTBidAdjustmentErrorCode,
          ErrorNumber: this.MTBidAdjustmentErrorNumber,
          Property: `${this.targetTypePrefix}${targetRowKey}ErrorObservable`,
        });
      }

      if (!cashbackPercentValidated) {
        const errorMessage = getCashbackPercentValidationError(rowCashbackPercent, i18n, decimal);

        cashbackErrors.push({
          Error: errorMessage,
          ErrorNumber: '',
          Property: `${this.targetTypePrefix}${targetRowKey}ErrorObservable`,
        });
      }
    });

    bidErrors = bidErrors.concat(cashbackErrors);
    bidErrors = getBulkApiErrors(bidErrors);
    bidErrors = this.populateErrorMessageConstants(bidErrors);

    this.errors.add(bidErrors);

    return isValid;
  }

  isCashbackValueEmpty(v) {
    return v == null || v === '' || !this.supportCashbackSettings();
  }

  isCashbackValuesEqual(v1, v2) {
    if (v1 == null) {
      v1 = ''; // eslint-disable-line
    }
    if (v2 == null) {
      v2 = ''; // eslint-disable-line
    }

    if (!this.supportCashbackSettings()) {
      return true;
    }
    return v1 === v2;
  }

  resetCashbackInheritInfoInAllRows(inheritInfo) {
    if (inheritInfo != null) {
      const { value, level } = inheritInfo;

      if (level === 'AdGroup') {
        this.currentAdGroup = _.extend({}, this.currentAdGroup, {
          AdGroupCashback: _.extend({}, this.currentAdGroup ? this.currentAdGroup.AdGroupCashback : {}, {
            Percent: value,
          }),
        });
      } else if (level === 'Campaign') {
        this.currentCampaign = _.extend({}, this.currentCampaign, {
          CampaignCashback: _.extend({}, this.currentCampaign ? this.currentCampaign.CampaignCashback : {}, {
            Percent: value,
          }),
        });
      }
    }
    _.each(this.targetRowKeys, (targetRowKey) => {
      const row = this[`${this.targetTypePrefix}${targetRowKey}Row`];
      let campaignCashbackPercent = null;
      let adGroupCashbackPercent = null;

      if (this.options.level === 'campaign' || this.useCampaignSettings()) {
        campaignCashbackPercent = row.cashbackPercentValue();
      } else {
        campaignCashbackPercent = row.cashbackInheritValue();
        adGroupCashbackPercent = row.cashbackPercentValue();
      }
      const dataset = this.options;

      this.setCashbackInheritInfo({
        campaignCashbackPercent,
        adGroupCashbackPercent,
        dataset: this.options,
        row,
        targetRowKey,
      });
      row.cashbackInheritValue(this.getCashbackInheritValue(targetRowKey, dataset));
      row.cashbackOptions(this.getCashbackOptions(targetRowKey, dataset));
      row.selectedCashbackOption(this.getSelectedCashbackOption(targetRowKey, dataset));
    });
  }

  setCashbackInheritInfo({
    campaignCashbackPercent,
    adGroupCashbackPercent,
    dataset,
    targetRowKey,
  }) {
    const campaignCashbackInheritOptions = getCashbackInheritOptions({
      viewContext: {
        currentAdGroup: this.currentAdGroup,
        currentCampaign: this.currentCampaign,
      },
      i18n,
      readonly: this.readonlyBid(),
      CashbackPercent: campaignCashbackPercent,
    });
    const adGroupCashbackInheritOptions = getCashbackInheritOptions({
      viewContext: {
        currentAdGroup: this.currentAdGroup,
        currentCampaign: this.currentCampaign,
      },
      i18n,
      readonly: this.readonlyBid(),
      CashbackPercent: adGroupCashbackPercent,
    });

    dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackOptions`] = campaignCashbackInheritOptions.options;
    dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackOptions`] = adGroupCashbackInheritOptions.options;
    dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackInheritValue`] = campaignCashbackInheritOptions.inheritValue;
    dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackInheritValue`] = adGroupCashbackInheritOptions.inheritValue;
    dataset[`${this.targetTypePrefix}${targetRowKey}CampaignCashbackSelectedOption`] = campaignCashbackInheritOptions.selected;
    dataset[`${this.targetTypePrefix}${targetRowKey}AdGroupCashbackSelectedOption`] = adGroupCashbackInheritOptions.selected;
  }

  populateErrorMessageConstants(errors) {
    let result = [];

    _.each(errors, (bidError) => {
      //always use the first row's min and max bid values as all rows should have equal values
      //if the values for different rows are different (e.g. device targets), the child class should override this behaviour.
      bidError.message = bidError.message
        .replace('{0}', targetConstants.minimumBid[`${this.targetTypePrefix}_${this.targetRowKeys[0]}`])
        .replace('{1}', targetConstants.maximumBid[`${this.targetTypePrefix}_${this.targetRowKeys[0]}`]);
      result.push(bidError);
    });
    return result;
  }

  extractTargetingErrors(bulkResponse) {
    let errors = bulkResponse.errors;
    let remainingErrors = [];
    let criterionErrors = [];

    _.map(errors, (error) => {
      if (error.Error === this.MTBidAdjustmentErrorCode) {
        let errorEntity = _.find(bulkResponse.entity.CampaignCriterions, (campaignCriterion) => {
          return campaignCriterion['Client Id'] === error['Client Id'];
        });

        if (errorEntity) {
          error.Property = this.targetTypeToErrorProperty[errorEntity.Criterion[`${this.criterionType}Criterion`][`${this.criterionTypeTarget}`]];
        }
        criterionErrors.push(error);
      } else {
        remainingErrors.push(error);
      }
    });
    return {
      criterionErrors,
      remainingErrors,
    };
  }

  handleErrors(response) {
    let criterionErrorExtracts = this.extractTargetingErrors(response);
    let criterionResponseErrors = criterionErrorExtracts.criterionErrors;
    let criterionErrors = getBulkApiErrors(criterionResponseErrors);

    criterionErrors = this.populateErrorMessageConstants(criterionErrors);
    this.errors.add(criterionErrors);

    return criterionErrorExtracts.remainingErrors;
  }

  setShowAdGroupOverrideInformation() {
    // no-op in base class
  }
}
